import type { FC } from 'react';
import React, { memo, useEffect, useMemo, useRef } from 'react';
import { Bubble } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { FontSpec } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { DashboardWidgetComparisonType } from 'src/constants/dashboard';
import type { DashboardWidgetProps } from 'src/types/dashboard';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import ErrorOverlayWithRefetchButton from 'src/components/dashboard/DashboardPage/components/ErrorOverlayWithRefetchButton';
import {
  hideWidget,
  showWidget,
} from 'src/components/dashboard/DashboardPage/components/WidgetWrapper/helper';
import { getFocalAndComparisonAggregates } from 'src/components/dashboard/helper';
import useWidgetChartConfig from 'src/components/dashboard/hooks/useWidgetChartConfig';

import { generateBubbleWidgetDataset } from './helper';

import { styles } from './index.styles';

export const BubbleWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation('dashboard');
  const { data, loading, errorMsg, refetch } = useStatistics();
  const aggregates = getFocalAndComparisonAggregates(config, data);
  const { bubbleSize } = useWidgetChartConfig(config);
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const bubbleWidgetDataset = useMemo(
    () =>
      Array.isArray(aggregates) && aggregates.length
        ? generateBubbleWidgetDataset(aggregates, bubbleSize || 20, theme)
        : null,
    [aggregates, bubbleSize, theme],
  );
  const noData = !bubbleWidgetDataset || !bubbleWidgetDataset.length || !data;

  const { createStep, removeStep } = useGuidedTour();
  const guidedTour = useMemo(
    () => !noData && createStep(config.id, config.guided_tour_step),
    [noData, config.guided_tour_step],
  );

  const widgetRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (noData && !loading && !errorMsg) {
      removeStep(config.id);
      widgetRef.current = hideWidget(config.id, widgetRef.current);
    } else {
      showWidget(config.id, widgetRef.current);
    }
  }, [noData, loading, errorMsg]);

  if (loading || errorMsg) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Stack
          direction="column"
          spacing={2}
          data-test-id="loading-bubble"
          sx={styles.wrapper}
        >
          <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
            {config.name}
          </Typography>
          <Skeleton variant="rectangular" height="100%" />
        </Stack>
        {errorMsg && (
          <ErrorOverlayWithRefetchButton
            errorMsg={errorMsg}
            refetch={refetch}
            name={config.name}
          />
        )}
      </Box>
    );
  }

  // Do not render this widget, if the dataset is empty after calculating
  if (noData) {
    return null;
  }

  const labelValues: FontSpec = {
    family: '"Inter", sans-serif',
    size: upSm ? 16 : 12,
    weight: 'bold',
    style: 'normal',
    lineHeight: 1.2,
  };

  const comparisonOrgUnitName =
    config.comparison_type === DashboardWidgetComparisonType.ANCESTOR
      ? data.ancestor_result.org_units[0].name
      : data.children_results[0].org_units[0].name;

  return (
    <Box className="BubbleWidget-root" sx={styles.bubbleWidget}>
      {comparisonOrgUnitName === '' && (
        <Box
          className="BubbleWidget-missingComparison"
          sx={styles.missingComparison}
        >
          {t('ErrorMessages.MissingComparison')}
        </Box>
      )}

      <Stack direction="row" spacing={1.25}>
        {guidedTour}
        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
          {config.name}
        </Typography>
      </Stack>

      <Typography sx={{ py: 4, whiteSpace: 'pre-line' }}>
        {config.widget_text}
      </Typography>

      <Box className="BubbleWidget-wrapper" sx={styles.wrapper}>
        <Bubble
          data={{ datasets: bubbleWidgetDataset }}
          plugins={[ChartDataLabels]}
          options={{
            maintainAspectRatio: false,
            plugins: {
              datalabels: {
                textAlign: 'center',
                offset: 0,
                padding: 0,
              },
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: ({
                    datasetIndex,
                  }: {
                    datasetIndex: number | undefined;
                  }) =>
                    bubbleWidgetDataset[datasetIndex as number].label as string,
                },
                displayColors: false,
                titleFont: labelValues,
              },
            },
            scales: {
              x: {
                axis: 'x',
                ticks: {
                  stepSize: 10,
                },
                title: {
                  display: true,
                  font: labelValues,
                  text: t('Labels.GapTo', {
                    orgUnitName: comparisonOrgUnitName,
                  }),
                  color: theme.colors.black,
                  padding: {
                    top: upSm ? 8 : 4,
                  },
                },
                grid: {
                  // show tick lines and single grid line
                  display: true,
                  drawTicks: true,
                  color: (context) =>
                    context.tick.value == 0
                      ? theme.colors.neutral[300]
                      : 'transparent',
                  tickColor: theme.colors.neutral[300],
                },
              },
              y: {
                axis: 'y',
                suggestedMax: 100,
                title: {
                  display: true,
                  font: labelValues,
                  color: theme.colors.black,
                  text: t('Labels.FavPercentFull'),
                },
                grid: {
                  // show tick lines and single grid line
                  display: true,
                  drawTicks: true,
                  color: (context) =>
                    context.tick.value == 70
                      ? theme.colors.neutral[300]
                      : 'transparent',
                  tickColor: theme.colors.neutral[300],
                },
              },
            },
          }}
        />

        <Typography sx={styles.disadvantageLabel}>
          {t('Labels.Disadvantage')}
        </Typography>

        <Typography sx={styles.advantageLabel}>
          {t('Labels.Advantage')}
        </Typography>
      </Box>
    </Box>
  );
};

export default memo(BubbleWidget);
